import { useState } from 'react'
import { ChevronDown, ChevronRight, Plus, Users } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { ErpTable, EmptyRow, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th }
  from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { fmtDate } from '@/shared/lib/format'
import type { SurveyAssignmentDto, SurveyDispositionDto } from '@/shared/api/types'
import { getFieldProgress, getQuotaStatus, listPolls, listStrata } from './api'
import { AssignDialog } from './AssignDialog'
import { CrewDialog } from './CrewDialog'
import { AttemptsPanel } from './AttemptsPanel'
import { PollSelect } from './PollSelect'
import { RevokeDialog } from './RevokeDialog'
import { Pill, assignmentStatusLabel, count, pct } from './vocab'

/**
 * Who is fielding this poll, and how it is going.
 *
 * ## The rates are the point, not the progress bar
 *
 * PollSphere's Distribution tab is read-only, forces every assignment to
 * `ASSIGNED` forever, and renders a field no endpoint returns. It has no concept
 * of an attempted-but-not-completed contact at all, which is why nothing in it
 * can report a response rate.
 *
 * A bar at 60% of target says nothing about whether the missing 40% refused, were
 * never home, or were never approached — and those are three different problems
 * with three different fixes. So the strip leads with AAPOR RR1, COOP1 and REF1,
 * and the bar is one card among five.
 *
 * ## This screen is where `survey_contact_attempt` gets its rows
 *
 * Until the attempts panel below existed, nothing in the application could write
 * to that table, which is why `getDisposition` came back with five nulls and a
 * suppression sentence. The rates were not zero and were not bad; their
 * denominators were empty. Assign, revoke and record-an-attempt are the three
 * writes that fill them.
 *
 * ## A null rate renders as a sentence
 *
 * Never as 0%. The server returns null when a denominator is empty, and
 * `suppressedReason` says why. "0% response rate" and "we have not started" are
 * different statements, and the disposition panel prints the second rather than
 * drawing the first.
 */
export function FieldworkPage() {
  const canRead = useCan('survey:read')
  const canUpdate = useCan('survey:update')
  const canField = useCan('survey:field')
  const [params, setParams] = useSearchParams()
  const pollId = params.get('poll')

  const [assigning, setAssigning] = useState(false)
  const [postingCrew, setPostingCrew] = useState(false)
  const [revoking, setRevoking] = useState<SurveyAssignmentDto | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)

  const polls = useLoad(() => listPolls({}, 0, 100), [])
  const progress = useLoad(
    () => (pollId ? getFieldProgress(pollId) : Promise.resolve(null)), [pollId])
  const quota = useLoad(
    () => (pollId ? getQuotaStatus(pollId) : Promise.resolve(null)), [pollId])
  /** Only to populate the assign dialog's stratum picker; empty means unstratified. */
  const strata = useLoad(() => (pollId ? listStrata(pollId) : Promise.resolve([])), [pollId])

  if (!canRead) return <Forbidden what="fieldwork" />

  const setPoll = (id: string) => {
    const next = new URLSearchParams(params)
    if (id) next.set('poll', id)
    else next.delete('poll')
    setParams(next, { replace: true })
    setExpanded(null)
  }

  const data = progress.data
  const rates = data?.disposition
  const poll = polls.data?.content.find((p) => p.id === pollId) ?? null
  const cells = (quota.data?.dimensions ?? []).flatMap((d) =>
    d.cells.map((c) => ({ dimension: d.name || d.code, ...c })))
  const closed = cells.filter((c) => !c.openForCapture).length

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Fieldwork"
        description="Assignments, contact attempts, disposition rates and quota fill — for one poll at a time."
        right={<PollSelect polls={polls.data?.content ?? []} value={pollId} onChange={setPoll} />}
      />

      <ErrorStrip message={progress.error ?? quota.error} />

      {!pollId ? (
        <Note tone="info">
          Pick a poll. Fieldwork figures are per poll on purpose — a response rate averaged
          across polls with different instruments and different populations is a number nobody
          can act on.
        </Note>
      ) : progress.loading && !data ? <LoadingBlock label="Loading fieldwork…" /> : data && (
        <>
          <KpiStrip>
            <MetricCard label="Collected"
              value={count(data.collected)}
              sub={`of ${count(data.targetSample)} target`} />
            <MetricCard label="Response rate"
              value={pct(rates?.responseRate1)} sub="AAPOR RR1 — completes ÷ eligible" />
            <MetricCard label="Cooperation"
              value={pct(rates?.cooperationRate1)} sub="COOP1 — of everybody reached" />
            <MetricCard label="Refusal"
              value={pct(rates?.refusalRate1)} sub="REF1"
              accent={overRefusing(rates?.refusalRate1)} />
            <MetricCard label="Interviewers"
              value={count(data.activeAssignments)}
              sub={`${count(data.assignmentCount)} assigned in total`} />
          </KpiStrip>

          {/*
            The strip's rates render as em dashes when they are null, and an em
            dash on its own reads as a rendering fault. The sentence says which
            it is; the full breakdown, rate by rate, is in the disposition panel.
          */}
          {rates?.suppressedReason && <Note tone="info">{rates.suppressedReason}</Note>}

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 xl:col-span-7">
              <Panel
                title="Assignments"
                description="Expand a row for the attempts underneath it — they are what the rates are computed from."
                right={canUpdate && poll && (
                  <div className="flex items-center gap-1.5">
                    {/*
                      Two buttons rather than one dialog that does both. "Assign"
                      names a stratum and a per-person target; "Post a crew"
                      names several people and one place. Folding them together
                      would mean a form where half the fields are inert
                      depending on how many names are in the picker.
                    */}
                    <button type="button" className="erp-btn h-8 text-erp-xs"
                      onClick={() => setPostingCrew(true)}>
                      <Users className="h-3 w-3" /> Post a crew
                    </button>
                    <button type="button" className="erp-btn h-8 text-erp-xs"
                      onClick={() => setAssigning(true)}>
                      <Plus className="h-3 w-3" /> Assign
                    </button>
                  </div>
                )}
              >
                <ErpTable head={(
                  // A FRAGMENT of <Th>, not a <tr>: `ErpTable` supplies the row
                  // itself, and a nested <tr> is invalid markup React silently
                  // reparents.
                  <>
                    <Th />
                    <Th>Interviewer</Th>
                    <Th>Where</Th>
                    <Th>Stratum</Th>
                    <Th>Status</Th>
                    <Th className="text-right">Collected</Th>
                    <Th className="text-right">Target</Th>
                    <Th>Due</Th>
                    <Th />
                  </>
                )}>
                  {data.assignments.length === 0 ? (
                    <EmptyRow colSpan={9} message={
                      'Nobody is assigned. A poll cannot go live without at least one '
                      + 'interviewer — an empty field is a progress screen that reads 0 of 400 '
                      + 'indefinitely.'
                    } />
                  ) : data.assignments.flatMap((a) => [
                    <tr key={a.id} className="hover:bg-muted/40">
                      <Td>
                        <button
                          type="button"
                          className="erp-btn erp-btn-ghost h-7 w-7 p-0"
                          aria-expanded={expanded === a.id}
                          aria-label={`${expanded === a.id ? 'Hide' : 'Show'} attempts for `
                            + `${a.mobilizerName ?? 'this assignment'}`}
                          onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                        >
                          {expanded === a.id
                            ? <ChevronDown className="h-3.5 w-3.5" />
                            : <ChevronRight className="h-3.5 w-3.5" />}
                        </button>
                      </Td>
                      <Td>{a.mobilizerName ?? '—'}</Td>
                      <Td className="text-muted-foreground">{a.scopeUnitName ?? '—'}</Td>
                      <Td className="font-mono text-erp-xs text-muted-foreground">
                        {a.stratumCode ?? '—'}
                      </Td>
                      <Td><Pill>{assignmentStatusLabel(a.statusCode)}</Pill></Td>
                      <Td className="text-right tabular-nums">{count(a.collected)}</Td>
                      <Td className="text-right tabular-nums text-muted-foreground">
                        {a.targetCount === 0 ? '—' : count(a.targetCount)}
                      </Td>
                      <Td className="text-muted-foreground">
                        {a.dueAt ? fmtDate(a.dueAt) : '—'}
                      </Td>
                      <Td className="text-right whitespace-nowrap">
                        {canUpdate && (a.statusCode === 'REVOKED' ? (
                          <span className="text-erp-xs text-muted-foreground">
                            revoked · kept
                          </span>
                        ) : (
                          // Offered on COMPLETED and EXPIRED assignments too. The
                          // server decides what is revocable; hiding the control
                          // on a status guess is how a legal action becomes
                          // invisible.
                          <button type="button"
                            className="erp-btn erp-btn-ghost h-7 text-erp-xs text-chocolate"
                            onClick={() => setRevoking(a)}>
                            Revoke
                          </button>
                        ))}
                      </Td>
                    </tr>,
                    expanded === a.id ? (
                      <tr key={`${a.id}-attempts`}>
                        <td colSpan={9} className="border-b border-border bg-muted/20 p-3">
                          <AttemptsPanel
                            surveyId={pollId}
                            assignment={a}
                            canRecord={canField}
                            // Every attempt moves a disposition count and can move
                            // the collected total, both of which live on the
                            // field-progress read above.
                            onRecorded={() => progress.reload()}
                          />
                          {a.revokeReason && (
                            <p className="mt-2 text-erp-xs text-muted-foreground">
                              Revoked: {a.revokeReason}
                            </p>
                          )}
                        </td>
                      </tr>
                    ) : null,
                  ])}
                </ErpTable>
              </Panel>
            </div>

            <div className="col-span-12 xl:col-span-5 space-y-4">
              <DispositionPanel rates={rates ?? null} />

              <Panel
                title="Quota fill"
                description={quota.data
                  ? `Enforcement: ${quota.data.enforcement}. A full cell is refused at capture when BLOCK.`
                  : undefined}
              >
                <ErpTable head={(
                  <>
                    <Th>Cell</Th>
                    <Th className="text-right">Achieved</Th>
                    <Th className="text-right">Target</Th>
                    <Th>State</Th>
                  </>
                )}>
                  {cells.length === 0 ? (
                    <EmptyRow colSpan={4} message={
                      'No quotas declared. The sample will be whoever the interviewers reach, '
                      + 'which is a design decision worth making on purpose.'
                    } />
                  ) : cells.map((cell) => (
                    <tr key={cell.id} className="hover:bg-muted/40">
                      <Td>
                        <span className="text-muted-foreground">{cell.dimension}</span>
                        {' · '}
                        {cell.label ?? cell.valueCode}
                      </Td>
                      <Td className="text-right tabular-nums">{count(cell.achieved)}</Td>
                      <Td className="text-right tabular-nums text-muted-foreground">
                        {cell.target === null ? '—' : count(cell.target)}
                      </Td>
                      <Td>
                        {cell.openForCapture
                          ? <span className="text-muted-foreground text-erp-xs">open</span>
                          : <Pill tone="bg-amber-500/10 text-amber-600 border-amber-500/30">full</Pill>}
                      </Td>
                    </tr>
                  ))}
                </ErpTable>
                {closed > 0 && (
                  <div className="mt-3">
                    <Note tone="bad">
                      {closed} cell{closed === 1 ? ' is' : 's are'} full. Under BLOCK the server
                      refuses an interview into a full cell with a 409 naming it, so the field
                      app can route the interviewer somewhere still open rather than discarding
                      work already done.
                    </Note>
                  </div>
                )}
              </Panel>
            </div>
          </div>

          {assigning && poll && (
            <AssignDialog
              open
              poll={poll}
              strata={strata.data ?? []}
              onClose={() => setAssigning(false)}
              onSaved={() => { setAssigning(false); progress.reload() }}
            />
          )}

          {postingCrew && poll && (
            <CrewDialog
              open
              poll={poll}
              onClose={() => setPostingCrew(false)}
              onSaved={() => progress.reload()}
            />
          )}

          {revoking && (
            <RevokeDialog
              open
              surveyId={pollId}
              assignment={revoking}
              onClose={() => setRevoking(null)}
              onSaved={() => { setRevoking(null); progress.reload() }}
            />
          )}
        </>
      )}
    </div>
  )
}

/**
 * Whether the refusal card gets the screen's one accent.
 *
 * Written out rather than inlined as `Number(x ?? 0) > 0.3` because that idiom is
 * the one this feature bans: a missing rate coerced to zero is a claim the server
 * did not make, and the fact that it happens to be safe *here* — no refusals is
 * not alarming — is exactly how the pattern survives to somewhere it is not. No
 * figure means no judgement, so a null is not accented.
 *
 * 0.3 is a threshold for a border colour, not a published number. It does not
 * appear on screen and nothing downstream reads it.
 */
function overRefusing(rate: string | number | null | undefined): boolean {
  if (rate === null || rate === undefined) return false
  const n = Number(rate)
  return Number.isFinite(n) && n > 0.3
}

/**
 * Every rate the server publishes, with the denominator it used.
 *
 * ## Why the formulas are printed here and nowhere else in this feature
 *
 * Rule 2 forbids the client computing a percentage, and nothing here does — every
 * figure and every base below is a component of `DispositionResponse`, and the
 * only arithmetic on this page is the em dash. What IS printed is the *definition*
 * of each rate, and this is the one screen where that earns its place: the entire
 * professional value of "44.9% response rate" is that the reader and the writer
 * agree on what it counted. A rate whose denominator is not stated is a number two
 * people can quote at each other for a week without discovering they mean
 * different things.
 *
 * ## Where the denominators come from
 *
 * `DispositionResponse` returns `eligibleBase` and `reachedBase` alongside the
 * seven counts and the five rates, and those two bases are what the rates divide
 * by. The wording below is read off those components rather than off an AAPOR
 * paper: the contract gives the fields, not the formulas, and describing what the
 * server's own bases contain is a claim this file can actually stand behind.
 * Anyone changing the server's arithmetic should change these sentences with it —
 * they are the specification the reader is given.
 *
 * ## A null rate is not a zero
 *
 * Five nulls and a `suppressedReason` is what an unstarted poll looks like, and
 * what every poll in this product looked like until attempts could be recorded.
 * The sentence goes in the value's place, not in a tooltip beside a 0%.
 */
function DispositionPanel({ rates }: { rates: SurveyDispositionDto | null }) {
  if (!rates) return null

  const eligible = count(rates.eligibleBase)
  const reached = count(rates.reachedBase)

  return (
    <Panel
      title="Disposition"
      description="What happened on every approach, and the rates those approaches add up to."
    >
      <dl className="grid gap-3"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
        <Stat label="Complete" value={count(rates.complete)} />
        <Stat label="Partial" value={count(rates.partial)} />
        <Stat label="Refused" value={count(rates.refusal)} />
        <Stat label="Not reached" value={count(rates.nonContact)} />
        <Stat label="Other non-response" value={count(rates.otherNonResponse)} muted />
        <Stat label="Ineligible" value={count(rates.ineligible)} muted />
        <Stat label="Callback" value={count(rates.callback)} muted />
      </dl>

      <div className="mt-4 space-y-1">
        <p className="text-erp-xs uppercase tracking-wide text-muted-foreground">Rates</p>
        <RateRow
          code="RR1" name="Response rate 1" value={rates.responseRate1}
          denominator={`Completes ÷ eligible base of ${eligible}.`}
          suppressedReason={rates.suppressedReason}
        />
        <RateRow
          code="RR3" name="Response rate 3" value={rates.responseRate3}
          denominator={`Completes and partials ÷ eligible base of ${eligible}. `
            + 'A partial counts as a response here and does not in RR1 — that is the whole '
            + 'difference between the two.'}
          suppressedReason={rates.suppressedReason}
        />
        <RateRow
          code="COOP1" name="Cooperation rate 1" value={rates.cooperationRate1}
          denominator={`Completes ÷ reached base of ${reached} — of everybody an interviewer `
            + 'actually spoke to, how many gave an interview.'}
          suppressedReason={rates.suppressedReason}
        />
        <RateRow
          code="REF1" name="Refusal rate 1" value={rates.refusalRate1}
          denominator={`Refusals ÷ eligible base of ${eligible}.`}
          suppressedReason={rates.suppressedReason}
        />
        <RateRow
          code="CON1" name="Contact rate 1" value={rates.contactRate1}
          denominator={`Reached base of ${reached} ÷ eligible base of ${eligible} — how much of `
            + 'the sample was reachable at all, independent of what they then said.'}
          suppressedReason={rates.suppressedReason}
        />
      </div>

      <div className="mt-3">
        <Note tone="info">
          <strong>Eligible base ({eligible})</strong> is everybody who was in the population and
          approached: completes, partials, refusals, not-reached and other non-response.
          <strong> Reached base ({reached})</strong> is the part of that an interviewer actually
          spoke to: completes, partials and refusals. Ineligible ({count(rates.ineligible)}) and
          callback ({count(rates.callback)}) sit outside both — somebody who was never in the
          population was never a possible interview, and a callback has not finished happening.
          Moving one attempt between "not reached" and "ineligible" moves it between those two
          denominators, which is why the attempt dialog spells the difference out.
        </Note>
      </div>
    </Panel>
  )
}

/**
 * One rate, or the sentence explaining why there is not one.
 *
 * `suppressedReason` is a single field for the whole response rather than one per
 * rate, so it is shown against whichever rates came back null. Where the server
 * gave neither a figure nor a reason, `pct(null)` renders an em dash and this
 * component adds nothing — inventing a sentence would be worse than the dash,
 * because a reader would take it for the server's.
 */
function RateRow({ code, name, value, denominator, suppressedReason }: {
  code: string
  name: string
  value: string | number | null
  denominator: string
  suppressedReason: string | null
}) {
  const missing = value === null || value === undefined

  return (
    <div className="grid grid-cols-[3.5rem_1fr_auto] items-baseline gap-2 py-1.5
      border-b border-border last:border-b-0">
      <span className="font-mono text-erp-xs text-muted-foreground">{code}</span>
      <span className="min-w-0">
        <span className="block text-erp-sm">{name}</span>
        <span className="block text-erp-xs text-muted-foreground">
          {missing && suppressedReason ? suppressedReason : denominator}
        </span>
      </span>
      <span className="tabular-nums text-erp-md whitespace-nowrap">
        {pct(value)}
      </span>
    </div>
  )
}

function Stat({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="rounded border border-border bg-muted/30 p-3">
      <dt className="text-erp-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={`text-erp-md tabular-nums mt-1 ${muted ? 'text-muted-foreground' : ''}`}>
        {value}
      </dd>
    </div>
  )
}
